/**
 * Paged loading of the sidebar's conversation list, as the app does it (`useSidebarConversations`,
 * `SIDEBAR_PAGE_SIZE`): the server slices `GET /:customer/conversations/list?limit&offset`
 * newest-first, "more" is inferred from a full page (the route returns no total).
 *
 * A refresh re-fetches the whole loaded window (`limit = max(PAGE_SIZE, loaded)`, offset 0), so
 * a periodic refresh never shrinks a list the user has scrolled through.
 */
export const CONVERSATION_PAGE_SIZE = 20;

export interface ConversationPage {
    limit: number;
    offset: number;
}

export function refreshPage(loadedCount: number): ConversationPage {
    return { limit: Math.max(CONVERSATION_PAGE_SIZE, loadedCount), offset: 0 };
}

export function nextPage(loadedCount: number): ConversationPage {
    return { limit: CONVERSATION_PAGE_SIZE, offset: loadedCount };
}

/** A page shorter than asked for is the last one. */
export function pageHasMore(rows: readonly unknown[], page: ConversationPage): boolean {
    return rows.length >= page.limit;
}

/** Appends the rows of a later page, skipping ids already in the list (a row that moved between pages). */
export function appendPage<T extends { _id?: unknown }>(previous: readonly T[], rows: readonly T[]): T[] {
    const seen = new Set(previous.map((row) => String(row?._id)));
    const fresh = rows.filter((row) => !seen.has(String(row?._id)));
    return fresh.length ? [...previous, ...fresh] : [...previous];
}
